import isNil from 'lodash-es/isNil';
import {
    CertificateInfoDto,
    FirmwareInfoDto,
    FirmwareUpdateState,
    RouterDto,
    RouterInitState,
    SimcardDto,
    VpnInfoDto,
    VpnLinkedState,
} from '../api/core';
import { RouterTypeDto } from '../api/core/models/router-type-dto';
import { LoggedInUser, UserRoles } from '../users';
import { checkPermissions } from '../users/user.utils';
import { clearEmptyValue } from '../utils';

export interface DeviceState {
    loading: number;
    devices: RouterDto[];
    totalCount: number;
}

export enum DeviceActionTypes {
    LOADING = '@liebherr/devices/loading',
    LOADING_DONE = '@liebherr/devices/loading_done',
    LOAD_DEVICES = '@liebherr/devices/load_devices',
    LOADED_DEVICES = '@liebherr/devices/loaded_devices',
    LOAD_SINGLE_DEVICE = '@liebherr/devices/load_single_device',
    LOADED_SINGLE_DEVICE = '@liebherr/devices/loaded_single_device',
    ADD_DEVICE = '@liebherr/devices/add_device',
    ADDED_DEVICE = '@liebherr/devices/added_device',
    ADD_DEVICES_BULK = '@liebherr/devices/add_devices_bulk',
    ADDED_DEVICES_BULK = '@liebherr/devices/added_devices_bulk',
    EDIT_DEVICE = '@liebherr/devices/edit_device',
    EDIT_DEVICE_DONE = '@liebherr/devices/edit_device_done',
    LOAD_MASTER_PASSWORD = '@liebherr/devices/load_master_password',
    LOADED_MASTER_PASSWORD = '@liebherr/devices/loaded_master_password',
    DELETE_DEVICE = '@liebherr/devices/delete_device',
    DELETED_DEVICE = '@liebherr/devices/deleted_device',

    ACTIVATE_DEVICE = '@liebherr/devices/activate_device',
    ACTIVATED_DEVICE = '@liebherr/devices/activated_device',
    ALLOW_CERIFICATE_RENEWAL = '@liebherr/devices/allow_certificate_renewal',
    ALLOWED_CERIFICATE_RENEWAL = '@liebherr/devices/allowed_certificate_renewal',
    ALLOW_FW_ACTIVATION = '@liebherr/devices/allow_fw_activation',
    ALLOWED_FW_ACTIVATION = '@liebherr/devices/allowed_fw_activation',
    ALLOW_FW_DOWNLOAD = '@liebherr/devices/allow_fw_download',
    ALLOWED_FW_DOWNLOAD = '@liebherr/devices/allowed_fw_download',
    FORCE_FW_UPDATE = '@liebherr/devices/force_fw_update',
    FORCED_FW_UPDATE = '@liebherr/devices/forced_fw_update',
    CONNECT_DEVICE = '@liebherr/devices/connect_device',
    CONNECTED_DEVICE = '@liebherr/devices/connected_device',
    DISCONNECT_DEVICE = '@liebherr/devices/disconnect_device',
    DISCONNECTED_DEVICE = '@liebherr/devices/disconnected_device',

    UPDATE_DEVICE = '@liebherr/devices/update_device',
    UPDATE_LINK = '@liebherr/devices/update_link',
}

export const devicesFeatureKey = 'devices';

export enum DeviceColumns {
    // DEFAULT
    ONLINE = 'ONLINE',
    SERIAL_NUMBER = 'SERIAL_NUMBER',
    CRANE_ID = 'CRANE_ID',
    LINK = 'LINK',
    SIM_CARD_EXPIRATION_DATE = 'SIM_CARD_EXPIRATION_DATE',
    CONNECTED_USER = 'CONNECTED_USER',
    STATE_TIME = 'STATE_TIME',
    VPN_IP = 'VPN_IP',
    ACTIONS = 'ACTIONS',
    ROUTER_STATE = 'ROUTER_STATE',
    TENANT = 'TENANT',
    // ROUTER_TYPE
    ROUTER_TYPE = 'ROUTER_TYPE',
    // CERTIFICATE
    C_COMBINED = 'C_COMBINED',
    C_REQUEST_TIME = 'C_REQUEST_TIME',
    C_GRANTED_TIME = 'C_GRANTED_TIME',
    C_GRANTED_UNTIL = 'C_GRANTED_UNTIL',
    C_GRANTED_BY = 'C_GRANTED_BY',
    C_FETCHED_TIME = 'C_FETCHED_TIME',
    // FIRMWARE
    FW_COMBINED = 'FW_COMBINED',
    FW_TARGET_VERSION = 'FW_TARGET_VERSION',
    FW_DOWNLOAD_ALLOWED_TIME = 'FW_DOWNLOAD_ALLOWED_TIME',
    FW_DOWNLOAD_ALLOWED_BY = 'FW_DOWNLOAD_ALLOWED_BY',
    FW_DOWNLOAD_TIME = 'FW_DOWNLOAD_TIME',
    FW_INSTALLED_TIME = 'FW_INSTALLED_TIME',
}

export class RouterCreation {
    routerType?: string;
    firmwareVersion?: string;
    simExpirationDate?: string;
    iccid?: string;
    id?: string;
    serialNumber?: string;
    routerVendor?: string;
    constructor(dto: any) {
        this.routerType = clearEmptyValue(dto?.routerType || dto[CSVHeader.ROUTER_TYPE]);
        this.simExpirationDate = clearEmptyValue(dto?.simExpirationDate || dto[CSVHeader.SIMEXPIRATIONDATE]);
        this.firmwareVersion = clearEmptyValue(dto?.firmwareVersion || dto[CSVHeader.FIRMWAREVERSION]);
        this.iccid = clearEmptyValue(dto?.iccid || dto[CSVHeader.ICCID]);
        this.id = clearEmptyValue(dto?.id || dto[CSVHeader.ID]);
        this.serialNumber = clearEmptyValue(dto?.serialNumber || dto[CSVHeader.SERIALNUMBER]);
        // important: vendors can be set in uppercase or lowercase in csv import -> ensure they are always saved in lowercase!
        this.routerVendor = clearEmptyValue(dto?.routerVendor || dto?.vendor || dto[CSVHeader.VENDOR])?.toLowerCase();
    }
}

export enum CSVHeader {
    ROUTER_TYPE = 'Typ',
    FIRMWAREVERSION = 'FW Version',
    SIMEXPIRATIONDATE = 'Datum SIM',
    ICCID = 'SIM Card No.',
    ID = 'IMEI:Nr.',
    SERIALNUMBER = 'S/N:Nr.',
    VENDOR = 'Provider',
}

export interface DeviceActionMenuItem {
    active: boolean;
    label: string;
    event: DeviceMenuAction;
}

export class Router {
    certificateInfo?: CertificateInfoDto;
    routerInitState?: RouterInitState;
    online: boolean;
    craneId?: string;
    fwUpdateInfo?: FirmwareInfoDto;
    firmwareUpdateState?: FirmwareUpdateState;
    id?: string;
    serialNumber?: string;
    simcardInfo?: SimcardDto;
    vpnInfo?: VpnInfoDto;
    vpnState?: VpnLinkedState;
    menuItems: DeviceActionMenuItem[];
    routerType?: RouterTypeDto;
    servicePath?: string;

    constructor(dto: RouterDto, loggedInUser?: LoggedInUser) {
        this.certificateInfo = dto.certificateInfo;
        this.routerInitState = dto['routerInitState'];
        this.online = dto.online || false;
        this.craneId = dto.craneId;
        this.fwUpdateInfo = dto.fwUpdateInfo;
        this.firmwareUpdateState = dto['firmwareUpdateState'];
        this.id = dto.id;
        this.serialNumber = dto.serialNumber;
        this.simcardInfo = dto.simcardInfo;
        this.vpnInfo = dto.vpnInfo;
        this.routerType = dto.routerType;
        this.servicePath = dto.servicePath;
        this.vpnState = dto['vpnState'];
        this.menuItems = this.createActionMenuItems(loggedInUser);
    }

    private createActionMenuItems(loggedInUser?: LoggedInUser) {
        const baseLabel = 'DEVICES.ACTION_MENU.';
        const enabledActions = this.getEnabledDeviceMenuActions(loggedInUser);
        const hiddenActions = this.getHiddenDeviceMenuActions(loggedInUser);

        return [
            DeviceMenuAction.MASTER_PASSWORT,
            DeviceMenuAction.EDIT,
            DeviceMenuAction.ACTIVATE_DEVICE,
            DeviceMenuAction.CERTIFICATE_RENEWAL,
            DeviceMenuAction.VPN_DISCONNECT,
            DeviceMenuAction.FW_DONWLOAD,
            DeviceMenuAction.FW_ACTIVATION,
            DeviceMenuAction.FW_FORCE,
            DeviceMenuAction.DELETE_DEVICE,
        ]
            .map((action) => {
                return {
                    // user has no permission -> action must not be displayed
                    hidden: hiddenActions.indexOf(DeviceMenuAction[action]) === -1,
                    // device matches requirements && user has permission -> action is active
                    active: enabledActions.indexOf(DeviceMenuAction[action]) > -1,
                    label: baseLabel + action,
                    event: DeviceMenuAction[action],
                };
            })
            ?.filter((action) => !action.hidden);
    }

    private getEnabledDeviceMenuActions(loggedInUser?: LoggedInUser) {
        return Object.values(DeviceMenuAction)?.filter((action) => {
            let requirements = false;

            const basicStates = [RouterInitState.Registered, RouterInitState.CertRequested, RouterInitState.RequestExpired];

            switch (action) {
                case DeviceMenuAction.MASTER_PASSWORT: {
                    requirements = this.routerInitState ? !basicStates.includes(this.routerInitState) : false;
                    break;
                }
                case DeviceMenuAction.DELETE_DEVICE: {
                    requirements = true;
                    break;
                }
                case DeviceMenuAction.EDIT: {
                    requirements = true;
                    break;
                }
                case DeviceMenuAction.ACTIVATE_DEVICE: {
                    requirements = this.routerInitState ? basicStates.includes(this.routerInitState) : false;
                    break;
                }
                case DeviceMenuAction.CERTIFICATE_RENEWAL: {
                    requirements = this.routerInitState === RouterInitState.NormalRenewal || this.routerInitState === RouterInitState.Grace;
                    break;
                }
                case DeviceMenuAction.FW_DONWLOAD: {
                    requirements =
                        (this.routerInitState === RouterInitState.Normal || this.routerInitState === RouterInitState.NormalRenewal) &&
                        this.firmwareUpdateState === FirmwareUpdateState.NeedFwUpdate;
                    break;
                }
                case DeviceMenuAction.FW_ACTIVATION: {
                    requirements =
                        (this.routerInitState === RouterInitState.Normal || this.routerInitState === RouterInitState.NormalRenewal) &&
                        this.firmwareUpdateState === FirmwareUpdateState.NeedFwActivation;
                    break;
                }
                case DeviceMenuAction.FW_FORCE: {
                    requirements =
                        this.firmwareUpdateState === FirmwareUpdateState.Actual ||
                        this.firmwareUpdateState === FirmwareUpdateState.NeedFwActivation ||
                        this.firmwareUpdateState === FirmwareUpdateState.NeedFwUpdate;
                    break;
                }
                case DeviceMenuAction.VPN_DISCONNECT: {
                    requirements =
                        (this.routerInitState === RouterInitState.Normal || this.routerInitState === RouterInitState.NormalRenewal) &&
                        this.vpnState === VpnLinkedState.Linked;
                    break;
                }
                default: {
                    break;
                }
            }
            const roles: UserRoles[] = getDevicePermissions(action, loggedInUser, this);
            const hasPermission = loggedInUser ? checkPermissions(loggedInUser, roles) : false;
            return requirements && hasPermission;
        });
    }

    getHiddenDeviceMenuActions(loggedInUser?: LoggedInUser) {
        return Object.values(DeviceMenuAction)?.filter((action) => {
            const roles: UserRoles[] = getDevicePermissions(action, loggedInUser, this);
            const hasPermission = loggedInUser ? checkPermissions(loggedInUser, roles) : false;

            return hasPermission;
        });
    }
}
export enum DeviceMenuAction {
    // base
    MASTER_PASSWORT = 'MASTER_PASSWORT',
    EDIT = 'EDIT',
    DELETE_DEVICE = 'DELETE_DEVICE',
    ACTIVATE_DEVICE = 'ACTIVATE_DEVICE',
    CERTIFICATE_RENEWAL = 'CERTIFICATE_RENEWAL',
    // fw
    FW_DONWLOAD = 'FW_DONWLOAD',
    FW_ACTIVATION = 'FW_ACTIVATION',
    FW_FORCE = 'FW_FORCE',
    // vpn
    VPN_DISCONNECT = 'VPN_DISCONNECT',
}
export enum DeviceButtonEvent {
    CERTIFICATE = 'CERTIFICATE',
    RESERVE_VPN = 'RESERVE_VPN',
    CONNECT = 'CONNECT',
    RELEASE_FW = 'RELEASE_FW',
    UPDATE_FW = 'UPDATE_FW',
    FORCE_FW = 'FORCE_FW',
}

export enum SearchOptions {
    SERIALNUMBER = 'SERIALNUMBER',
    ROUTERID = 'ROUTERID',
}

export function getDevicePermissions(
    action: DeviceMenuAction | 'loadAll' | 'addDevice' | 'loadSingleDevice',
    loggedInUser?: LoggedInUser,
    device?: Partial<Router>
) {
    switch (action) {
        case 'loadAll':
        case 'loadSingleDevice': {
            return [UserRoles.LCO_GET_1];
        }
        case 'addDevice': {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.EDIT: {
            return [UserRoles.LCO_PUT_2];
        }
        case DeviceMenuAction.ACTIVATE_DEVICE: {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.CERTIFICATE_RENEWAL: {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.DELETE_DEVICE: {
            return [UserRoles.LCO_DELETE_3];
        }
        case DeviceMenuAction.FW_ACTIVATION: {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.FW_DONWLOAD: {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.FW_FORCE: {
            return [UserRoles.LCO_POST_2];
        }
        case DeviceMenuAction.MASTER_PASSWORT: {
            return [UserRoles.LCO_GET_2];
        }
        case DeviceMenuAction.VPN_DISCONNECT: {
            // when loggedInUser is connected to device, only LCO_DELETE_1 is needed, else LCO_DELETE_2 is needed
            return !isNil(device?.vpnInfo?.currentUser) && device?.vpnInfo?.currentUser === loggedInUser?.username
                ? [UserRoles.LCO_DELETE_1]
                : [UserRoles.LCO_DELETE_2];
        }

        default: {
            return [];
        }
    }
}

export enum ErrorKeys {
    routerTypeRequired = 'routerTypeRequired',
    routerTypeInvalid = 'routerTypeInvalid',
    routerTypeVendorMatch = 'routerTypeVendorMatch',
    serialNumber = 'serialNumber',
    serialNumberDuplicate = 'serialNumberDuplicate',
    imei = 'imei',
    imeiDuplicate = 'imeiDuplicate',
    iccid = 'iccid',
    iccidDuplicate = 'iccidDuplicate',
    date = 'date',
    vendor = 'vendor',
    vendorRouterTypeMatch = 'vendorRouterTypeMatch',
}

export type DeviceErrors = { [key in ErrorKeys]: boolean };
export type DeviceErrorCollection = { [key in ErrorKeys]?: number };
